import { useContext, useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';

import { useBetterMediaQuery } from '@swordhealth/ui-corporate';
import {
  Inner,
  Wrapper,
  VideoContainer,
  Overlay,
  WrappedSection,
  WrappedContainer,
  LogosContainer,
} from './styles';

import { Logos } from '@/app/components/shared/Logos/Logos';
import ClientContext from '@/contexts/client.context';
import getImgSrcWorkaround from '@/utils/images';
import theme from '@/utils/styles-variables';
import { HeroWithOverlayContent } from './HeroWithOverlayContent';

const videoAnimationProps = {
  animate: { opacity: 1, y: 0 },
  autoPlay: true,
  initial: { opacity: 0, y: 75 },
  loop: false,
  muted: true,
  playsInline: true,
  transition: { duration: 0.8, ease: 'easeInOut' },
};

const HeroWithOverlay = ({ config, content, userName }) => {
  const { openModal, client } = useContext(ClientContext);
  const { media, ...contentRest } = content?.heroContent ?? {};
  const videoRef = useRef(undefined);

  const isTabletOrMobile = useBetterMediaQuery(`(max-width: ${theme.breakpoints.min_lg}px)`);

  useEffect(() => {
    if (videoRef?.current) {
      videoRef.current.defaultMuted = true;
    }
  }, []);

  const logos = useMemo(() => {
    const logoData = client.logo?.data || client.logos?.data;

    if (!logoData) {
      return [];
    }

    return Array.isArray(logoData) ? logoData.slice(0, 3) : [logoData];
  }, [client?.logo, client?.logos]);

  const logosComponentProps = useMemo(
    () => ({
      centered: true,
      showSwordLogo: client.showSwordLogo ?? true,
      client,
      withBackground: true,
      logos: client.clientKey ? logos : [],
    }),
    [client, logos],
  );

  const MotionVideoWrapper = motion(VideoContainer);

  const MemoizedHeroWithOverlay = useMemo(() => {
    if (isTabletOrMobile) {
      return (
        <>
          <WrappedContainer>
            <Overlay />

            <Inner>
              <LogosContainer>
                <Logos className="hero-logos" {...logosComponentProps} />
              </LogosContainer>

              <MotionVideoWrapper ref={videoRef} {...videoAnimationProps}>
                <source src={getImgSrcWorkaround(media?.data?.attributes?.url)} />
              </MotionVideoWrapper>
            </Inner>
          </WrappedContainer>

          <HeroWithOverlayContent
            client={client}
            openModal={openModal}
            userName={userName}
            {...contentRest}
          />
        </>
      );
    }

    return (
      <>
        <LogosContainer>
          <Logos className="hero-logos" {...logosComponentProps} />
        </LogosContainer>

        <WrappedContainer>
          <Inner>
            <HeroWithOverlayContent
              client={client}
              openModal={openModal}
              userName={userName}
              {...contentRest}
            />

            <MotionVideoWrapper {...videoAnimationProps}>
              <source src={getImgSrcWorkaround(media?.data?.attributes?.url)} />
            </MotionVideoWrapper>
          </Inner>
        </WrappedContainer>
      </>
    );
  }, [
    client,
    contentRest,
    isTabletOrMobile,
    logosComponentProps,
    media?.data?.attributes?.url,
    openModal,
    userName,
  ]);

  return (
    <WrappedSection spaceBottom={config?.spaceBottom}>
      {!isTabletOrMobile && <Overlay />}

      <Wrapper>{MemoizedHeroWithOverlay}</Wrapper>
    </WrappedSection>
  );
};

HeroWithOverlay.defaultConfig = {
  colored: false,
  marginBottom: true,
  rounded: true,
  spaceBottom: false,
  spaceTop: false,
  withGradient: false,
};

HeroWithOverlay.lockedConfig = {
  rounded: false,
  withGradient: false,
};

export default HeroWithOverlay;
